import React from 'react';
import { Edit2, Trash2, Eye, Mail, Phone, Building, Globe, UserCheck, Star } from 'lucide-react';

const EnseignantExterneCard = ({ enseignant, onEdit, onDelete, onViewDetails }) => {
  const getTitreStyle = (titre) => {
    switch (titre) {
      case 'DR': return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-200';
      case 'PR': return 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-200';
      case 'MC': return 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-indigo-200';
      case 'HDR': return 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-emerald-200';
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-gray-200';
    }
  };

  const getStatutStyle = (statut) => {
    switch (statut) {
      case 'ACTIF': return {
        dot: 'bg-emerald-500',
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200'
      };
      case 'INACTIF': return {
        dot: 'bg-red-500',
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200'
      };
      case 'SUSPENDU': return {
        dot: 'bg-orange-500',
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200'
      };
      default: return {
        dot: 'bg-gray-500',
        bg: 'bg-gray-50',
        text: 'text-gray-700',
        border: 'border-gray-200'
      };
    }
  };

  const statutStyle = getStatutStyle(enseignant.statut);

  return (
    <div className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-gray-300 transition-all duration-300 overflow-hidden">
      {/* Header moderne */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 p-5 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4 min-w-0 flex-1">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center shadow-sm">
                <UserCheck className="h-7 w-7 text-gray-600" />
              </div>
              <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${statutStyle.dot} border-2 border-white shadow-sm`}></div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-gray-900 leading-tight mb-2 truncate">
                {enseignant.nom_complet}
              </h3>
              <div className="flex items-center space-x-3 mb-2">
                <span className={`px-3 py-1 text-sm font-semibold rounded-lg shadow-sm ${getTitreStyle(enseignant.titre)}`}>
                  {enseignant.titre}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-md border ${statutStyle.bg} ${statutStyle.text} ${statutStyle.border}`}>
                  {enseignant.statut}
                </span>
              </div>
                             <p className="text-base text-gray-700 font-medium flex items-start leading-relaxed">
                 <Globe className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5" />
                 <span className="break-words">{enseignant.specialite}</span>
               </p>
            </div>
          </div>
          
          {/* Actions élégantes */}
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-all duration-300 ml-3">
            <button
              onClick={() => onViewDetails(enseignant)}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-105"
              title="Voir les détails"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => onEdit(enseignant)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:scale-105"
              title="Modifier"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(enseignant)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-105"
              title="Désactiver"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Corps de la carte */}
      <div className="p-5 space-y-4">
        {/* Établissement */}
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Building className="h-4 w-4 text-gray-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 mb-1 truncate">
              {enseignant.etablissement_origine}
            </p>
            <p className="text-xs text-gray-500">Établissement d'origine</p>
          </div>
        </div>

        {/* Informations de contact */}
        <div className="space-y-2">
          {enseignant.utilisateur?.email && (
            <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
              <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="text-sm text-gray-700 truncate font-medium">
                {enseignant.utilisateur.email}
              </span>
            </div>
          )}
          {enseignant.telephone && (
            <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
              <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="text-sm text-gray-700 font-medium">
                {enseignant.telephone}
              </span>
            </div>
          )}
        </div>

        {/* Grade si disponible */}
        {enseignant.grade && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-gray-700">
                Grade: {enseignant.grade}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnseignantExterneCard; 